/**
 * Painting the occupancy image (global_path_harness' pose_picker "paint free" / "paint wall"):
 * erase clutter and lidar streaks, or draw walls the scan missed. An edit is sent as cell
 * indices and saved as a NEW map version (ADR-007: versions are immutable), so the web editor
 * and the backend must agree on what "free" and "wall" mean as pixel values.
 */
import { z } from "zod";
/**
 * The pixel values pose_picker (and map_saver) write for a negate: 0 map: 0 = wall, 254 = free.
 * 254 rather than 255 is map_saver's free value; both read as free under any sane free_thresh.
 */
export const PAINT_PIXEL = { free: 254, wall: 0 };
/**
 * The grey value to write for `kind` in an image with this `negate`. map_server reads
 * occupancy as `negate ? p/255 : 1 - p/255`, so a negated image stores the mirror values
 * (free 1, wall 255); without this a "free" stroke on such a map would draw walls.
 */
export function paintPixel(kind, negate) {
    const v = PAINT_PIXEL[kind];
    return negate ? 255 - v : v;
}
/**
 * Upper bound on the cells one edit may change: a 40 cm brush at 5 cm/px covers ~200 cells per
 * dab, so 2M is far beyond any hand-painted session while keeping the JSON body (~16 MB worst
 * case) and the server's work bounded.
 */
export const MAP_EDIT_MAX_CELLS = 2_000_000;
const CellIndices = z.array(z.number().int().nonnegative()).max(MAP_EDIT_MAX_CELLS);
/**
 * POST /maps/:id/versions/:v/edits. Cells are row-major pixel indices of the version's image
 * (index = row * width + col, row 0 = the TOP row, as in the PGM), grouped by what to paint them.
 * Plain index lists rather than [u, v, value] triples: half the bytes, and the value set is
 * closed (wall/free) so it needs no per-cell field. The server checks the bounds (it knows the
 * image size) and refuses a cell listed under both.
 */
export const MapEditRequest = z
    .object({
    free: CellIndices.default([]),
    wall: CellIndices.default([]),
})
    .refine((b) => b.free.length + b.wall.length > 0, { message: "Paint at least one cell", path: ["free"] })
    .refine((b) => b.free.length + b.wall.length <= MAP_EDIT_MAX_CELLS, { message: `At most ${MAP_EDIT_MAX_CELLS} cells per edit`, path: ["free"] });
//# sourceMappingURL=mapedit.js.map